import { Check, Library, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { type GenerationResult, useComposerAddToLibrary } from "@/lib/api";

/**
 * Put a generated file back where the next market will find it.
 *
 * Sits beside Download because those are the two things to do with a finished
 * file, and the one that helps everyone else is the one nobody remembers
 * unless it is on screen. It queues rather than publishes: the market that
 * generated the file can say it is worth keeping, but not that it counts as
 * approved brand material — so the button reports what actually happened
 * rather than claiming the file is in the library now.
 */
export function AddToLibrary({
  result,
  size = "default",
}: {
  result: GenerationResult;
  size?: "default" | "sm";
}) {
  const addToLibrary = useComposerAddToLibrary();
  const submission = addToLibrary.data;

  if (submission) {
    return (
      <p className="text-muted flex items-start gap-1.5 text-[11px] leading-4">
        <Check className="text-outcome-applied mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{submission.message}</span>
      </p>
    );
  }

  return (
    <Button
      variant="outline"
      size={size}
      className="shrink-0"
      onClick={() => addToLibrary.mutate({ artifactId: result.artifact_id })}
      disabled={addToLibrary.isPending}
    >
      {addToLibrary.isPending ? (
        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
      ) : (
        <Library className="mr-1.5 h-3.5 w-3.5" />
      )}
      Add to library
    </Button>
  );
}
